import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";

function Shell({ children }) {
  return (
    <div className="flex min-h-[100svh] items-center justify-center px-5 pt-24">
      <div className="w-full max-w-md rounded-2xl border border-white/[0.07] bg-fog/60 p-10 text-center">
        {children}
      </div>
    </div>
  );
}

export function ConfirmSubscribe() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | ok | error
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMsg("No confirmation token found in this link.");
      return;
    }
    api
      .get("/subscribe/confirm", { params: { token } })
      .then((res) => {
        setState("ok");
        setMsg(res.data.email);
      })
      .catch((err) => {
        setState("error");
        setMsg(err?.response?.data?.detail || "This confirmation link is invalid or has expired.");
      });
  }, [params]);

  return (
    <Shell>
      {state === "loading" && (
        <div data-testid={T.confirmState}>
          <Loader2 size={28} className="mx-auto animate-spin text-cyanx" />
          <p className="mt-6 font-serif text-2xl font-light text-ink">Confirming…</p>
        </div>
      )}
      {state === "ok" && (
        <div data-testid={T.confirmState}>
          <CheckCircle2 size={34} className="mx-auto text-cyanx" />
          <h1 className="mt-6 font-serif text-3xl font-light text-ink">You're on the list.</h1>
          <p className="mt-3 text-sm leading-relaxed text-dim">
            {msg} is confirmed. A welcome email is on its way — thank you for joining
            Isabella's reader list.
          </p>
          <Link
            to="/books"
            className="mt-8 inline-block rounded-full bg-cyanx px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-void"
          >
            Explore the books
          </Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid={T.confirmState}>
          <XCircle size={34} className="mx-auto text-ember" />
          <h1 className="mt-6 font-serif text-3xl font-light text-ink">That didn't work.</h1>
          <p className="mt-3 text-sm leading-relaxed text-dim">{msg}</p>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-full border border-white/15 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:border-cyanx/60 hover:text-cyanx"
          >
            Get in touch
          </Link>
        </div>
      )}
    </Shell>
  );
}

export function Unsubscribe() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMsg("No unsubscribe token found in this link.");
      return;
    }
    api
      .get("/unsubscribe", { params: { token } })
      .then((res) => {
        setState("ok");
        setMsg(res.data.email);
      })
      .catch((err) => {
        setState("error");
        setMsg(err?.response?.data?.detail || "This unsubscribe link is invalid.");
      });
  }, [params]);

  return (
    <Shell>
      {state === "loading" && (
        <div data-testid={T.unsubscribeState}>
          <Loader2 size={28} className="mx-auto animate-spin text-cyanx" />
          <p className="mt-6 font-serif text-2xl font-light text-ink">Working on it…</p>
        </div>
      )}
      {state === "ok" && (
        <div data-testid={T.unsubscribeState}>
          <MailCheck size={34} className="mx-auto text-cyanx" />
          <h1 className="mt-6 font-serif text-3xl font-light text-ink">Unsubscribed.</h1>
          <p className="mt-3 text-sm leading-relaxed text-dim">
            {msg} has been removed from the reader list. No hard feelings — the books will
            still be here.
          </p>
          <Link
            to="/"
            className="mt-8 inline-block rounded-full border border-white/15 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:border-cyanx/60 hover:text-cyanx"
          >
            Back home
          </Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid={T.unsubscribeState}>
          <XCircle size={34} className="mx-auto text-ember" />
          <h1 className="mt-6 font-serif text-3xl font-light text-ink">That didn't work.</h1>
          <p className="mt-3 text-sm leading-relaxed text-dim">{msg}</p>
        </div>
      )}
    </Shell>
  );
}
